import { type GetHistory } from "@common/components/Activity/History";
import Select from "@common/controls/Inputs/Select/component";
import { useI18n } from "@common/hooks/i18n";
import { usePromise } from "@common/lib/hooks";
import Network from "@common/modules/network/types";
import classNames from "classnames";
import React from "react";
import { Users } from "../user/types";
import { AnalyticsWidget } from "./Widget/Analytics";
import { CommissionsWidget } from "./Widget/Commission";
import { CompetitionWidget } from "./Widget/Competition";
import { HistoryWidget } from "./Widget/History";
import { OffersWidget } from "./Widget/Offer";
import { ProfileWidget } from "./Widget/Profile/component";
import { adjustCycleOptionsDisplay } from "./Widget/lib";
import { isAvailable } from "./lib";
import { type Affiliate } from "./types";

export type AffiliateDashboardApi = {
  audit: Affiliate.AuditApi;
  getHistory: GetHistory;
  getLeaderboardCount: (id: string) => Promise<number>;
  getLeaderboardItem: (id: string) => Promise<Network.Incentives.Api.IncentiveLeaderboardItem>;
  getPayoutCycles: (data: Network.Payouts.Api.GetCyclesRequest) => Promise<Network.Payouts.Api.Cycle[]>;
  getProfileData: (cycleEndDate: string) => Promise<Users.Api.DashboardAffiliate>;
};

type Props = {
  affiliateLinks: Network.Offers.Api.AffiliateLink[];
  api: AffiliateDashboardApi;
  className?: string;
  cycleEndDate: string;
  cycleOptions: Network.Cycles.Api.CycleOption[];
  fieldStyle?: React.CSSProperties;
  heading?: any;
  historyLabelKeyMapper?: (labelKey: string) => string;
  kpiOptions?: Affiliate.KpiOptionItem[];
  incentives: Network.Incentives.Api.Incentive[];
  isHost?: boolean;
  isMobile?: boolean;
  gap: number | string;
  offers: Network.Offers.Api.UserOffer[];
  offersTitle?: string;
  reportOptions: Affiliate.ReportOptionItem[];

  onCycleChange: (cycleEndDate: string) => void;
  onIncentiveClick: (id: string) => void;
  onEditProfileClick: () => void;
  onKpiClick?: (id: string) => void;
  onReportClick: (id: string, cycleEndDate: string, profile: Users.Api.DashboardAffiliate) => void;

  widgetStyle?: React.CSSProperties;
};
const AffiliateDashboard = (props: Props) => {
  const i18n = useI18n();

  const cycleOptions = React.useMemo(
    () => adjustCycleOptionsDisplay(i18n, props.cycleOptions),
    [props.cycleOptions, i18n]
  );

  const [profile, isProfileLoading] = usePromise<Users.Api.DashboardAffiliate>({
    fn: props.api.getProfileData,
    args: [props.cycleEndDate],
    shouldLoad: true
  });

  const kpiOptions = React.useMemo(() => {
    return props.kpiOptions?.filter((x) => !profile || isAvailable(profile, x.availability));
  }, [props.kpiOptions, profile]);

  const reportOptions = React.useMemo(() => {
    return props.reportOptions.filter((x) => !profile || isAvailable(profile, x.availability));
  }, [props.reportOptions, profile]);

  return (
    <div
      className={classNames("affiliate_dashboard", props.className, { mobile: props.isMobile })}
      style={{ gap: props.gap }}
    >
      <div className="toolbar">
        <h3>{props.heading}</h3>
        <div className="actions">
          <Select
            isRequired
            onChange={props.onCycleChange}
            options={cycleOptions}
            style={props.fieldStyle}
            value={props.cycleEndDate}
          />
        </div>
      </div>

      {props.incentives.map((x) => (
        <CompetitionWidget
          key={x.incentiveId}
          competition={x}
          getLeaderboardItem={props.api.getLeaderboardItem}
          getLeaderboardCount={props.api.getLeaderboardCount}
          onClick={() => props.onIncentiveClick!(x.incentiveId)}
          style={props.widgetStyle}
        />
      ))}

      <div className="widgets_container" style={{ gap: props.gap }}>
        <ProfileWidget
          cycleEndDate={props.cycleEndDate}
          data={profile}
          kpis={kpiOptions}
          isLoading={isProfileLoading}
          onClick={props.onKpiClick}
          onEditClick={props.onEditProfileClick}
          style={props.widgetStyle}
        />
        <HistoryWidget
          cycleEndDate={props.cycleEndDate}
          getHistory={props.api.getHistory}
          labelKeyMapper={props.historyLabelKeyMapper}
          style={props.widgetStyle}
        />
      </div>

      <div className="widgets_container" style={{ gap: props.gap }}>
        <AnalyticsWidget
          onClick={(value) => props.onReportClick(value as any, props.cycleEndDate, profile!)}
          options={reportOptions}
          style={props.widgetStyle}
        />

        <CommissionsWidget
          api={props.api.audit}
          cycleEndDate={props.cycleEndDate}
          getPayoutCycles={props.api.getPayoutCycles}
          style={props.widgetStyle}
        />
      </div>

      <OffersWidget
        offers={props.offers}
        title={props.offersTitle}
        affiliateLinks={props.affiliateLinks}
        style={props.widgetStyle}
      />
    </div>
  );
};
export default AffiliateDashboard;
